import { useState } from "react";
import { Heart, MessageCircle, Trash2, Send, X, Users, Pin, PinOff } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useConfirm } from "./ConfirmDialog";

// Helpers (pure)
function formatDate(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch (e) {
        return "";
    }
}

function updateCommentInTree(tree, id, updater) {
    const next = [];
    for (const c of tree) {
        if (c.comment_id === id) {
            next.push(updater(c));
            continue;
        }
        if (c.replies && c.replies.length > 0) {
            const newReplies = c.replies.map((r) =>
                r.comment_id === id ? updater(r) : r,
            );
            next.push({ ...c, replies: newReplies });
            continue;
        }
        next.push(c);
    }
    return next;
}

function deleteCommentFromTree(tree, id) {
    const filtered = tree.filter((c) => c.comment_id !== id);
    return filtered.map((c) => {
        if (!c.replies || c.replies.length === 0) return c;
        const newReplies = c.replies.filter((r) => r.comment_id !== id);
        return { ...c, replies: newReplies };
    });
}

// Single comment card (top-level or reply)
function CommentCard(props) {
    const { comment, moduleId, onChange, isReply } = props;
    const [replying, setReplying] = useState(false);
    const [replyText, setReplyText] = useState("");
    const [busy, setBusy] = useState(false);
    const [showLikers, setShowLikers] = useState(false);
    const [likers, setLikers] = useState(null); // null = not yet loaded
    const [loadingLikers, setLoadingLikers] = useState(false);
    const confirm = useConfirm();

    const toggleLike = async () => {
        try {
            const { data } = await api.post(
                "/comments/" + comment.comment_id + "/like",
            );
            onChange((prev) =>
                updateCommentInTree(prev, comment.comment_id, (c) => ({
                    ...c,
                    liked_by_me: data.liked,
                    likes_count: data.likes_count,
                })),
            );
            // Invalidate cached likers (will reload next time the user opens the popover)
            setLikers(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Impossible de liker");
        }
    };

    const openLikers = async () => {
        if (!comment.likes_count || comment.likes_count === 0) return;
        setShowLikers((v) => !v);
        if (likers === null && !loadingLikers) {
            setLoadingLikers(true);
            try {
                const { data } = await api.get(
                    "/comments/" + comment.comment_id + "/likers",
                );
                setLikers(data || []);
            } catch (e) {
                toast.error("Impossible de charger les likers");
                setLikers([]);
            } finally {
                setLoadingLikers(false);
            }
        }
    };

    const remove = async () => {
        const desc = isReply
            ? "Cette action est définitive."
            : "Cette action est définitive. Les réponses éventuelles seront également supprimées.";
        const ok = await confirm({
            title: "Supprimer ce commentaire ?",
            description: desc,
            confirmLabel: "Supprimer",
            tone: "danger",
        });
        if (!ok) return;
        try {
            await api.delete("/comments/" + comment.comment_id);
            onChange((prev) => deleteCommentFromTree(prev, comment.comment_id));
            toast.success("Commentaire supprimé");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Suppression impossible");
        }
    };

    const sendReply = async () => {
        const text = replyText.trim();
        if (!text) return;
        setBusy(true);
        try {
            const { data: created } = await api.post("/comments", {
                module_id: moduleId,
                content: text,
                parent_id: comment.comment_id,
            });
            onChange((prev) =>
                updateCommentInTree(prev, comment.comment_id, (c) => {
                    const existing = c.replies || [];
                    return { ...c, replies: existing.concat([created]) };
                }),
            );
            setReplyText("");
            setReplying(false);
            toast.success("Réponse publiée");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(detail || "Erreur d'envoi — vérifie ta connexion et réessaie.");
        } finally {
            setBusy(false);
        }
    };

    // Iter-26.8 — pin / unpin (admin + manager only, top-level only)
    const togglePin = async () => {
        const pinning = !comment.pinned_at;
        try {
            const { data } = await api.post(
                "/comments/" + comment.comment_id + "/" + (pinning ? "pin" : "unpin"),
            );
            // If pinning, also clear pin flag on any other top-level comment in the tree
            onChange((prev) => {
                let next = prev;
                if (pinning) {
                    next = prev.map((c) =>
                        c.comment_id !== comment.comment_id && c.pinned_at
                            ? { ...c, pinned_at: null, pinned_by_name: null }
                            : c,
                    );
                }
                return updateCommentInTree(next, comment.comment_id, (c) => ({
                    ...c,
                    pinned_at: pinning ? data.pinned_at : null,
                    pinned_by_name: pinning ? data.pinned_by_name : null,
                }));
            });
            toast.success(pinning ? "Commentaire épinglé 📌" : "Commentaire désépinglé");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action impossible");
        }
    };

    const avatarSize = isReply ? "w-8 h-8" : "w-9 h-9";
    const isPinned = Boolean(comment.pinned_at) && !isReply;
    const wrapperClass = isReply
        ? "border-l-2 border-shariti-orange/30 pl-4 ml-5 mt-2 py-2"
        : isPinned
          ? "rounded-2xl p-4 bg-gradient-to-br from-shariti-yellow/10 to-shariti-orange/5 border border-shariti-yellow/30 shadow-[0_4px_24px_rgba(252,219,83,0.08)]"
          : "glass-card rounded-2xl p-4";

    return (
        <div className={wrapperClass} data-testid={"comment-" + comment.comment_id}>
            {isPinned && (
                <div
                    className="flex items-center gap-1.5 mb-2.5 pb-2.5 border-b border-shariti-yellow/15"
                    data-testid={"comment-pinned-banner-" + comment.comment_id}
                >
                    <Pin className="w-3 h-3 text-shariti-yellow fill-shariti-yellow" />
                    <span className="text-[10px] uppercase tracking-[0.25em] font-extrabold text-shariti-yellow">
                        Épinglé
                        {comment.pinned_by_name
                            ? ` · par ${comment.pinned_by_name}`
                            : ""}
                    </span>
                </div>
            )}
            <div className="flex gap-3">
                {comment.user_avatar ? (
                    <img
                        src={comment.user_avatar}
                        alt={comment.user_name}
                        className={avatarSize + " rounded-xl object-cover shrink-0"}
                    />
                ) : (
                    <div
                        className={
                            avatarSize +
                            " rounded-xl bg-gradient-to-br from-shariti-orange to-shariti-yellow flex items-center justify-center font-bold text-shariti-black text-sm shrink-0"
                        }
                    >
                        {comment.user_name ? comment.user_name[0] : "?"}
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-white text-sm">
                            {comment.user_name}
                        </p>
                        {comment.created_at && (
                            <span className="text-[10px] text-white/30">
                                {formatDate(comment.created_at)}
                            </span>
                        )}
                    </div>
                    <p className="text-sm text-white/80 mt-1 whitespace-pre-wrap break-words">
                        {comment.content}
                    </p>
                    <div className="flex items-center gap-3 sm:gap-3 mt-2 relative flex-wrap">
                        <button
                            onClick={toggleLike}
                            data-testid={"comment-like-" + comment.comment_id}
                            className={
                                "inline-flex items-center gap-1 text-xs font-bold transition-all " +
                                (comment.liked_by_me
                                    ? "text-shariti-orange"
                                    : "text-white/40 hover:text-shariti-orange-light")
                            }
                            aria-label={comment.liked_by_me ? "Retirer mon like" : "Liker"}
                        >
                            <Heart
                                className="w-3.5 h-3.5"
                                fill={comment.liked_by_me ? "currentColor" : "transparent"}
                            />
                        </button>
                        <button
                            type="button"
                            onClick={openLikers}
                            disabled={!comment.likes_count}
                            data-testid={"comment-likes-count-" + comment.comment_id}
                            className={
                                "text-xs font-bold transition-all -ml-2 " +
                                (comment.likes_count
                                    ? "text-white/60 hover:text-white cursor-pointer underline-offset-2 hover:underline"
                                    : "text-white/40 cursor-default")
                            }
                            title={comment.likes_count ? "Voir qui a aimé" : ""}
                        >
                            {comment.likes_count || 0}
                        </button>
                        {!isReply && (
                            <button
                                onClick={() => setReplying(!replying)}
                                data-testid={"comment-reply-" + comment.comment_id}
                                aria-label="Répondre"
                                className={
                                    "inline-flex items-center gap-1 text-xs font-bold transition-all " +
                                    (replying
                                        ? "text-shariti-orange"
                                        : "text-white/40 hover:text-shariti-orange-light")
                                }
                            >
                                <MessageCircle className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">Répondre</span>
                            </button>
                        )}
                        {(comment.can_pin && !isReply) || comment.can_delete ? (
                            <div className="ml-auto flex items-center gap-3">
                                {comment.can_pin && !isReply && (
                                    <button
                                        onClick={togglePin}
                                        data-testid={"comment-pin-" + comment.comment_id}
                                        title={isPinned ? "Désépingler" : "Épingler en haut du module"}
                                        aria-label={isPinned ? "Désépingler" : "Épingler"}
                                        className={
                                            "inline-flex items-center gap-1 text-xs font-bold transition-all " +
                                            (isPinned
                                                ? "text-shariti-yellow hover:text-white"
                                                : "text-white/40 hover:text-shariti-yellow")
                                        }
                                    >
                                        {isPinned ? (
                                            <PinOff className="w-3.5 h-3.5" />
                                        ) : (
                                            <Pin className="w-3.5 h-3.5" />
                                        )}
                                        <span className="hidden sm:inline">
                                            {isPinned ? "Désépingler" : "Épingler"}
                                        </span>
                                    </button>
                                )}
                                {comment.can_delete && (
                                    <button
                                        onClick={remove}
                                        data-testid={"comment-delete-" + comment.comment_id}
                                        aria-label="Supprimer"
                                        className="inline-flex items-center gap-1 text-xs font-bold text-white/40 hover:text-red-400 transition-all"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                        <span className="hidden sm:inline">Supprimer</span>
                                    </button>
                                )}
                            </div>
                        ) : null}
                        {/* Likers popover (iter-26.6) */}
                        {showLikers && (
                            <div
                                onMouseLeave={() => setShowLikers(false)}
                                data-testid={"comment-likers-popover-" + comment.comment_id}
                                className="absolute bottom-full left-0 mb-2 z-30 w-64 max-w-[80vw] rounded-2xl border border-white/10 bg-shariti-deep-black/95 backdrop-blur-xl shadow-2xl p-3"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-[10px] uppercase tracking-widest font-bold text-shariti-orange-light flex items-center gap-1.5">
                                        <Users className="w-3 h-3" />
                                        {comment.likes_count} like
                                        {comment.likes_count > 1 ? "s" : ""}
                                    </p>
                                    <button
                                        onClick={() => setShowLikers(false)}
                                        className="w-6 h-6 rounded-lg flex items-center justify-center hover:bg-white/5"
                                    >
                                        <X className="w-3 h-3 text-white/50" />
                                    </button>
                                </div>
                                {loadingLikers ? (
                                    <p className="text-xs text-white/40 italic py-2">
                                        Chargement…
                                    </p>
                                ) : likers && likers.length > 0 ? (
                                    <div className="space-y-1.5 max-h-64 overflow-y-auto">
                                        {likers.map((u) => (
                                            <div
                                                key={u.user_id}
                                                className="flex items-center gap-2"
                                            >
                                                {u.avatar ? (
                                                    <img
                                                        src={u.avatar}
                                                        alt={u.name}
                                                        className="w-7 h-7 rounded-lg object-cover shrink-0"
                                                    />
                                                ) : (
                                                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-shariti-orange to-shariti-yellow flex items-center justify-center font-bold text-shariti-black text-xs shrink-0">
                                                        {u.name ? u.name[0] : "?"}
                                                    </div>
                                                )}
                                                <span className="text-sm text-white/85 truncate">
                                                    {u.name}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-xs text-white/40 italic">
                                        Aucun like.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                    {replying && (
                        <div className="mt-3 flex gap-2 items-end overflow-hidden">
                                <textarea
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    placeholder={"Répondre à " + comment.user_name + "..."}
                                    rows={2}
                                    autoFocus
                                    data-testid={"comment-reply-input-" + comment.comment_id}
                                    className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-shariti-orange/50 resize-none"
                                />
                                <button
                                    onClick={sendReply}
                                    disabled={busy}
                                    data-testid={"comment-reply-submit-" + comment.comment_id}
                                    className="w-9 h-9 rounded-xl bg-shariti-orange flex items-center justify-center hover:bg-shariti-orange-light transition-all disabled:opacity-50"
                                >
                                    <Send className="w-3.5 h-3.5 text-white" />
                                </button>
                                <button
                                    onClick={() => {
                                        setReplying(false);
                                        setReplyText("");
                                    }}
                                    className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10 transition-all"
                                >
                                    <X className="w-3.5 h-3.5 text-white/60" />
                                </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function CommentThread({ comments, moduleId, onChange }) {
    return (
        <div className="space-y-3" data-testid="comment-thread">
            {comments.map((c) => (
                <div key={c.comment_id}>
                    <CommentCard
                        comment={c}
                        moduleId={moduleId}
                        onChange={onChange}
                        isReply={false}
                    />
                    {c.replies && c.replies.length > 0 && (
                        <div data-testid={"comment-replies-" + c.comment_id}>
                            {c.replies.map((r) => (
                                <CommentCard
                                    key={r.comment_id}
                                    comment={r}
                                    moduleId={moduleId}
                                    onChange={onChange}
                                    isReply={true}
                                />
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}
